import Image from '../../components/AppImage';

// ─────────────────────────────────────────────────────────────────────────────
// OnsitePM Shared PDF Design System — Logo Fetcher (library-agnostic)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches an organization logo from the 'organization-logos' Supabase storage
 * bucket and returns it as a base64 data URL usable by both jsPDF's addImage()
 * and @react-pdf/renderer's <Image src>.
 *
 * @param {object} supabaseClient  — initialized Supabase client
 * @param {string} logoStoragePath — path within the 'organization-logos' bucket
 * @returns {Promise<string|null>}  base64 data URL, or null on failure
 */
export const fetchOrgLogoAsBase64 = async (supabaseClient, logoStoragePath) => {
  if (!logoStoragePath) return null;

  try {
    const { data, error } = supabaseClient?.storage
      ?.from('organization-logos')
      ?.getPublicUrl(logoStoragePath);

    if (error || !data?.publicUrl) {
      console.warn('[fetchOrgLogoAsBase64] Could not get public URL:', error);
      return null;
    }

    const response = await fetch(data?.publicUrl);
    if (!response?.ok) {
      console.warn('[fetchOrgLogoAsBase64] Fetch failed:', response?.status);
      return null;
    }

    const blob = await response?.blob();

    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader?.result ?? null);
      reader.onerror  = () => {
        console.warn('[fetchOrgLogoAsBase64] FileReader error');
        resolve(null);
      };
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn('[fetchOrgLogoAsBase64] Unexpected error:', err);
    return null;
  }
};

export default fetchOrgLogoAsBase64;
